import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  Crown, ArrowLeft, Calendar, MapPin, Clock, StickyNote, Trash2,
  Loader2, UploadCloud, Link2, Film, ExternalLink, Send,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import apiClient, { API, SERVICES_META } from "@/lib/api";
import { toast } from "sonner";

const CHUNK_SIZE = 4 * 1024 * 1024;

export default function AdminBookingDetail() {
  const { bookingId } = useParams();
  const { user, loading } = useAuth();
  const [detail, setDetail] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/admin/bookings/${bookingId}/detail`);
      setDetail(data);
    } catch (e) {
      toast.error("Could not load booking");
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, bookingId]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await apiClient.post(`/admin/bookings/${bookingId}/notes`, { text: noteText.trim() });
      setNoteText("");
      await load();
      toast.success("Note added");
    } catch (e) {
      toast.error("Could not save note");
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await apiClient.delete(`/admin/bookings/${bookingId}/notes/${noteId}`);
      await load();
    } catch (e) {
      toast.error("Could not delete note");
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    setProgress(0);
    try {
      const uploadId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-up`);
      const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("total_chunks", total);
        fd.append("filename", file.name);
        fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        await apiClient.post(`/admin/bookings/${bookingId}/media/chunk`, fd);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      toast.success(`${file.name} uploaded`);
      await load();
    } catch (err) {
      const detailMsg = err?.response?.data?.detail;
      toast.error(typeof detailMsg === "string" ? detailMsg : "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm("Remove this file from the gallery?")) return;
    try {
      await apiClient.delete(`/admin/media/${mediaId}`);
      await load();
      toast.success("Removed");
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  const copyGalleryLink = () => {
    const url = `${window.location.origin}/gallery/${detail.gallery.id}`;
    navigator.clipboard.writeText(url);
    toast.success("Gallery link copied — share it with your guests!");
  };

  const b = detail?.booking;
  const timeline = detail ? buildTimeline(detail) : [];

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-booking-detail-page">
      <header className="border-b border-[#D4AF37]/15 px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
        <Link to="/admin/dashboard" className="flex items-center gap-3 text-sm text-zinc-400 hover:text-[#D4AF37]" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>
        <div className="flex items-center gap-3">
          <Crown className="w-5 h-5 text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Booking Detail</span>
        </div>
      </header>

      {!detail ? (
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      ) : (
        <div className="px-6 md:px-12 lg:px-24 py-10">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
                {SERVICES_META[b.service]?.title || b.service}
              </span>
              <h1 className="font-serif text-4xl md:text-5xl font-light mt-2">{b.full_name}</h1>
              <div className="text-xs text-zinc-500 mt-2 font-mono" data-testid="detail-booking-id">{b.id}</div>
            </div>
            <div className="text-right">
              <span className={`text-xs uppercase tracking-widest ${b.status === "cancelled" ? "text-zinc-500" : "text-[#D4AF37]"}`} data-testid="detail-status">
                {b.status} · {b.balance_paid ? "fully paid" : b.payment_status === "paid" ? "deposit paid" : b.payment_status}
              </span>
              <div className="font-serif text-3xl gold-gradient-text mt-2">${Number(b.total_amount).toFixed(2)}</div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left column — info + timeline */}
            <div className="lg:col-span-1 space-y-8">
              <div className="lk-card p-7 space-y-4 text-sm" data-testid="detail-info-card">
                <Info icon={Calendar} label="Event" value={`${b.event_type} · ${b.event_date} at ${b.start_time}`} />
                <Info icon={Clock} label="Duration" value={`${b.hours} hours · ${b.prints} prints · props: ${b.themed_props ? b.theme || "yes" : "no"}`} />
                <Info icon={MapPin} label="Venue" value={b.venue} />
                <Info label="Contact" value={`${b.email} · ${b.phone}`} />
                {b.notes && <Info label="Customer notes" value={b.notes} />}
                <div className="border-t border-[#D4AF37]/15 pt-4 grid grid-cols-3 gap-3">
                  <MoneyMini label="Total" value={b.total_amount} />
                  <MoneyMini label="Deposit" value={b.deposit_amount} />
                  <MoneyMini label="Balance" value={b.balance_paid ? 0 : b.balance_amount} />
                </div>
                {b.discount_amount > 0 && (
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]" data-testid="detail-promo">
                    Promo {b.promo_code} · −${Number(b.discount_amount).toFixed(2)}
                  </div>
                )}
              </div>

              <div className="lk-card p-7" data-testid="detail-timeline">
                <h2 className="font-serif text-2xl mb-6">Timeline</h2>
                <ol className="relative border-l border-[#D4AF37]/25 ml-1 space-y-6">
                  {timeline.map((t, i) => (
                    <li key={`${t.label}-${t.at}`} className="pl-6 relative" data-testid={`timeline-item-${i}`}>
                      <span className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ${t.gold ? "bg-[#D4AF37]" : "bg-zinc-600"}`} />
                      <div className="text-sm text-zinc-200">{t.label}</div>
                      {t.sub && <div className="text-xs text-[#D4AF37] mt-0.5">{t.sub}</div>}
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                        {new Date(t.at).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Right column — notes + gallery */}
            <div className="lg:col-span-2 space-y-8">
              <div className="lk-card p-7" data-testid="detail-notes">
                <h2 className="font-serif text-2xl mb-5 flex items-center gap-3">
                  <StickyNote className="w-5 h-5 text-[#D4AF37]" /> Private Notes
                </h2>
                <form onSubmit={addNote} className="flex gap-3 mb-6">
                  <input
                    type="text"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    className="lk-input flex-1"
                    placeholder="Add a note only you can see…"
                    data-testid="note-input"
                  />
                  <button type="submit" disabled={savingNote || !noteText.trim()} className="lk-btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-50" data-testid="note-submit">
                    {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Add
                  </button>
                </form>
                <ul className="space-y-3">
                  {detail.notes.length === 0 && <li className="text-sm text-zinc-500">No notes yet.</li>}
                  {[...detail.notes].reverse().map((n) => (
                    <li key={n.id} className="border border-[#D4AF37]/10 bg-[#D4AF37]/[0.03] p-4 flex items-start justify-between gap-4" data-testid={`note-${n.id}`}>
                      <div>
                        <div className="text-sm text-zinc-200 leading-relaxed">{n.text}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                          {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                      <button onClick={() => deleteNote(n.id)} className="text-zinc-600 hover:text-red-400 shrink-0" data-testid={`delete-note-${n.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lk-card p-7" data-testid="detail-gallery">
                <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
                  <h2 className="font-serif text-2xl flex items-center gap-3">
                    <Film className="w-5 h-5 text-[#D4AF37]" /> Event Gallery
                  </h2>
                  {detail.gallery && (
                    <div className="flex items-center gap-3">
                      <button onClick={copyGalleryLink} className="lk-btn-secondary text-xs inline-flex items-center gap-2" data-testid="copy-gallery-link">
                        <Link2 className="w-3.5 h-3.5" /> Copy Share Link
                      </button>
                      <a href={`/gallery/${detail.gallery.id}`} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-widest text-[#D4AF37] inline-flex items-center gap-1.5" data-testid="open-gallery-link">
                        View <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  )}
                </div>
                <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
                  Upload the 360° videos & photos from this event. Guests open the share link on their phone,
                  download their clips, and post straight to Instagram or TikTok.
                </p>

                <label className={`border border-dashed border-[#D4AF37]/40 p-8 flex flex-col items-center gap-3 cursor-pointer hover:bg-[#D4AF37]/[0.04] transition-colors ${uploading ? "pointer-events-none opacity-70" : ""}`}>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".mp4,.mov,.webm,.m4v,.jpg,.jpeg,.png,.gif"
                    className="hidden"
                    data-testid="media-file-input"
                    onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])}
                  />
                  {uploading ? (
                    <>
                      <Loader2 className="w-7 h-7 text-[#D4AF37] animate-spin" />
                      <span className="text-sm text-zinc-300" data-testid="upload-progress">Uploading… {progress}%</span>
                      <div className="w-full max-w-xs h-1 bg-zinc-800">
                        <div className="h-1 bg-[#D4AF37] transition-all" style={{ width: `${progress}%` }} />
                      </div>
                    </>
                  ) : (
                    <>
                      <UploadCloud className="w-7 h-7 text-[#D4AF37]" />
                      <span className="text-sm text-zinc-300">Click to upload a video or photo</span>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">mp4 · mov · webm · jpg · png — up to 200MB</span>
                    </>
                  )}
                </label>

                {detail.media.length > 0 && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-6">
                    {detail.media.map((m) => (
                      <div key={m.id} className="border border-[#D4AF37]/15 bg-black/40" data-testid={`media-item-${m.id}`}>
                        {m.kind === "video" ? (
                          <video src={`${API}/gallery/${detail.gallery.id}/media/${m.id}`} controls preload="metadata" className="w-full h-44 object-cover bg-black" />
                        ) : (
                          <img src={`${API}/gallery/${detail.gallery.id}/media/${m.id}`} alt={m.original_filename} className="w-full h-44 object-cover" />
                        )}
                        <div className="p-3 flex items-center justify-between gap-3">
                          <span className="text-xs text-zinc-400 truncate">{m.original_filename}</span>
                          <button onClick={() => deleteMedia(m.id)} className="text-zinc-600 hover:text-red-400 shrink-0" data-testid={`delete-media-${m.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function buildTimeline(detail) {
  const b = detail.booking;
  const events = [{ label: "Booking created", at: b.created_at }];
  detail.transactions.forEach((tx) => {
    const type = tx.metadata?.payment_type === "balance" ? "Balance" : "Deposit";
    if (tx.payment_status === "paid") {
      events.push({ label: `${type} payment received`, sub: `$${Number(tx.amount).toFixed(2)} via Stripe`, at: tx.updated_at || tx.created_at, gold: true });
    } else {
      events.push({ label: `${type} checkout started`, sub: `$${Number(tx.amount).toFixed(2)} · ${tx.payment_status}`, at: tx.created_at });
    }
  });
  if (b.reminder_email_sent_at) events.push({ label: "Balance reminder emailed", at: b.reminder_email_sent_at });
  if (b.cancelled_at) events.push({ label: "Booking cancelled", at: b.cancelled_at });
  if (b.feedback_email_sent_at) events.push({ label: "Feedback email sent", at: b.feedback_email_sent_at });
  return events.sort((a, x) => new Date(a.at) - new Date(x.at));
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="text-zinc-200 mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function MoneyMini({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-serif text-lg gold-gradient-text">${Number(value || 0).toFixed(2)}</div>
    </div>
  );
}
